// Cross-faction binding motif: La Virgen de Guadalupe.
//
// Used sparingly — max 2–3 marquee cards per faction. She is the binding
// visual that says "we all share this iconography regardless of which side
// of the parking lot we're on."
//
// Standard render per docs/visual-conventions.md:
//   - Vesica piscis halo of golden rays
//   - Turquoise mantle with gold 8-pointed stars
//   - Pink/cream tunic with gold filigree
//   - Downcast eyes (never direct gaze)
//   - Crescent moon underfoot supported by a single winged cherub
//
// Render as silhouette + flat color blocks — procedural, not photographic.
// She is NEVER the main subject of a card; she overlays as a halo/watermark
// behind another character or sits as a tattoo/patch detail.

interface VirgenMotifProps {
  /** Rendered size in px (square bounding box). */
  size?: number;
  /** Opacity — typically low (0.15–0.35) when used as watermark. */
  opacity?: number;
  /** Optional className for positioning. */
  className?: string;
}

const COLOR_MANTLE = '#1e8a99';     // turquoise mantle
const COLOR_RAYS = '#d4a945';        // tecate gold rays
const COLOR_TUNIC = '#e8b8a8';       // rose tunic
const COLOR_MOON = '#5a4a3c';        // edge gray crescent
const COLOR_SKIN = '#8b5a3c';        // La Morenita
const COLOR_STARS = '#f5ecd6';       // bone gold-stars (legible)

export function VirgenMotif({
  size = 96,
  opacity = 0.85,
  className,
}: VirgenMotifProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 96 96"
      fill="none"
      className={className}
      opacity={opacity}
      aria-hidden="true"
    >
      {/* Golden rays — vesica piscis halo, ~36 spokes */}
      <g>
        {Array.from({ length: 36 }).map((_, i) => {
          const angle = (i / 36) * 2 * Math.PI;
          const x1 = 48 + Math.cos(angle) * 24;
          const y1 = 48 + Math.sin(angle) * 30;
          const x2 = 48 + Math.cos(angle) * 44;
          const y2 = 48 + Math.sin(angle) * 50;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={COLOR_RAYS}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          );
        })}
      </g>
      {/* Mantle silhouette — vesica piscis (almond) shape */}
      <ellipse cx="48" cy="48" rx="22" ry="32" fill={COLOR_MANTLE} />
      {/* Gold stars on mantle — 8 scattered */}
      {[
        [38, 28], [56, 30], [34, 42], [60, 44],
        [40, 56], [56, 58], [44, 70], [52, 70],
      ].map(([cx, cy], i) => (
        <g key={`star-${i}`} transform={`translate(${cx} ${cy})`}>
          {/* 8-pointed star — two overlapping squares */}
          <rect x="-1.5" y="-1.5" width="3" height="3" fill={COLOR_STARS} />
          <rect x="-1.5" y="-1.5" width="3" height="3" fill={COLOR_STARS} transform="rotate(45)" />
        </g>
      ))}
      {/* Tunic — visible center strip */}
      <path
        d="M44 40 Q48 38 52 40 L54 72 Q48 76 42 72 Z"
        fill={COLOR_TUNIC}
      />
      {/* Tunic gold filigree — single vertical stripe */}
      <line x1="48" y1="42" x2="48" y2="70" stroke={COLOR_RAYS} strokeWidth="0.8" opacity="0.6" />
      {/* Face — small oval, downcast (no eyes drawn, abstract reverence) */}
      <ellipse cx="48" cy="34" rx="4" ry="5" fill={COLOR_SKIN} />
      {/* Hands — pressed at sternum (two small ovals) */}
      <ellipse cx="46" cy="48" rx="2" ry="3" fill={COLOR_SKIN} />
      <ellipse cx="50" cy="48" rx="2" ry="3" fill={COLOR_SKIN} />
      {/* Crescent moon underfoot */}
      <path
        d="M36 78 Q48 84 60 78 Q56 82 48 82 Q40 82 36 78 Z"
        fill={COLOR_MOON}
      />
      {/* Winged cherub — single tiny figure below mantle */}
      <circle cx="48" cy="84" r="2" fill={COLOR_SKIN} />
      <path d="M44 84 Q42 82 40 84 M52 84 Q54 82 56 84" stroke={COLOR_STARS} strokeWidth="1" fill="none" />
    </svg>
  );
}
